// src/types/bluebi/Painel.ts
// Contratos do módulo Blue B.I. (backend `api-blueltSystem`, pacote `bluebi`).
//
// ⚠️ O campo `link` é uma URL "Publish to web" do Power B.I.: ANÔNIMA, PÚBLICA e
// PERMANENTE. Quem tiver o endereço abre o relatório sem login, indefinidamente, e a
// aplicação NÃO consegue revogá-lo — desativar o painel apenas o tira da listagem de
// consumo. Consequências obrigatórias para quem consumir estes tipos:
//   - nunca passar `link` para `logger.*` nem para `console`;
//   - nunca colocá-lo em URL de navegação do próprio front (barra de endereços,
//     query string, histórico, state de rota);
//   - nunca ecoá-lo em mensagem de erro exibida ao usuário.
// Registrado na ADR-056 do backend e na seção 1 da issue #42.

/** Painel na rota de CONSUMO (`GET /v1/bluebi/paineis`): só os ativos e visíveis ao ator. */
export interface Painel {
  id: number;
  rotulo: string;
  link: string;
}

/**
 * Painel na rota de GESTÃO (`GET /v1/bluebi/gestao/paineis`), que é a única a mostrar
 * também os desativados — e por isso a única pela qual é possível reativá-los.
 */
export interface PainelGestao extends Painel {
  ativo: boolean;
}

/** Corpo de criação/edição (`POST /` e `PUT /{id}`). O backend substitui os dois campos. */
export interface PainelRequest {
  rotulo: string;
  link: string;
}

/** Vínculo painel ↔ usuário. O backend devolve só o id; o nome o front resolve na listagem do tenant. */
export interface Vinculo {
  usuarioId: number;
}

/** Teto da coluna `bluebi.painel.rotulo` no banco — espelhado no `maxLength` do campo. */
export const ROTULO_MAX_LENGTH = 65;

/** Host único aceito pelo backend para o link. Validar antes de enviar evita um round-trip. */
export const POWERBI_HOST = 'app.powerbi.com';

/**
 * Um painel fora de circulação não abre — para ninguém, nem por clique no card, nem por
 * URL direta, nem para `ROLE_MASTER`.
 *
 * O eixo aqui é o ESTADO do painel, não a permissão de quem pede: `bluebi.editar` dá o
 * direito de administrar o painel (inclusive de reativá-lo), não o de consumir um
 * relatório que foi retirado do ar. É a mesma regra que o backend aplica na rota de
 * consumo, que esconde inativos de todos; o front precisa dela porque a tela de gestão lê
 * a OUTRA rota, a que devolve inativos justamente para permitir reativá-los.
 *
 * `ativo` é opcional no tipo de consumo (aquela rota só devolve ativos, e omite o campo de
 * propósito): ausente significa ativo, e só `false` fecha.
 */
export const painelPodeAbrir = (painel: { ativo?: boolean } | null | undefined): boolean =>
  painel != null && painel.ativo !== false;

/**
 * Espelha a validação de link do `PainelGestaoService` para a recusa chegar antes do
 * round-trip. O backend continua sendo o gate real (premissa #1) — isto é UX.
 * Devolve a mensagem de recusa, ou `null` quando o link é aceitável.
 *
 * Nenhuma mensagem repete o valor recebido: o link é anônimo e público e não pode vazar
 * por dentro de um texto de erro (issue #42 §1).
 */
export const validarLinkPainel = (link: string): string | null => {
  const valor = link.trim();
  if (!valor) return 'Informe o link do painel.';
  let url: URL;
  try {
    url = new URL(valor);
  } catch {
    return 'O link do painel não é uma URL válida.';
  }
  if (url.protocol !== 'https:') return 'O link do painel deve usar https.';
  if (url.host !== POWERBI_HOST) return `O link do painel deve apontar para ${POWERBI_HOST}.`;
  if (url.username || url.password) return 'O link do painel não pode conter credenciais.';
  return null;
};

/**
 * Slug do rótulo, usado como identificador do painel na URL (`/bluebi/<slug>`).
 * O link nunca vai para a barra de endereços — só este slug vai.
 */
export const slugDoRotulo = (rotulo: string): string =>
  rotulo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
